import React, { useEffect, useMemo, useState } from 'react';
import {
  browserSessionPersistence,
  onAuthStateChanged,
  setPersistence,
  signInWithCustomToken,
} from 'firebase/auth';
import { fa } from '../firebase/fb';
import envConfig, { env } from '../firebase/config';
import {
  endImpersonation,
  getAcademicResults,
  getEncumbrances,
  getIdentity,
  getPortalPreferences,
} from '../api/api';
import AccessDenied from './AccessDenied';

const FIREBASE_CUSTOM_TOKEN = 'firebase_custom_token';

const checkEncumbrances = async () => {
  try {
    // If this call is successful you are ok to proceed

    await getAcademicResults();
    return {
      hasEncumbrances: false,
      embargo: false,
      isBarred: false,
    };
  } catch (e) {
    // If this api gives an error, you might be blocked
    const error = JSON.parse(e.body);
    const blocks = {
      hasEncumbrances: false,
      encumbranceEffectTypes: null,
      embargo: error?.errorCode === 'EMBARGO',
      isBarred: error?.errorCode === 'BARRED',
    };
    if (error?.errorCode === 'ENCUMBRANCE') {
      // need to find the types on encumberance
      const encumbrances = await getEncumbrances();
      return {
        ...blocks,
        ...encumbrances,
      };
    }
    return blocks;
  }
};

const StandardAuthProvider = (props) => {
  const { AuthContext, skipEncumbrancesCheck } = props;
  const auth = fa;
  const oktaLoginUrl = envConfig.oktaLoginUrl;
  const callbackPath = envConfig.callbackPath;
  const tokenApi = envConfig.tokenApi;
  const nolanding = props.nolanding;
  const loadingPage = props.loadingPage;
  const targetLocation = props.targetLocation;
  const accessControl = useMemo(
    () => props.accessControl || (() => true),
    [props.accessControl],
  );
  const getPreferences = props.getPreferences || false;

  const [error, setError] = useState(null);
  const [user, setUser] = useState(null);
  const [userDoc, setUserDoc] = useState(null);
  const [loadingUserDoc, setLoadingUserDoc] = useState(getPreferences); // init to flase if not waiting
  const [redirectUrl, setRedirectUrl] = useState(null);
  const [redirecting, setRedirecting] = useState(false);

  const showLoadingPage = useMemo(
    () => redirecting || loadingUserDoc,
    [redirecting, loadingUserDoc],
  );

  const getUserToken = async (user) => {
    try {
      const [identity, idToken, encumbrances] = await Promise.all([
        getIdentity(),
        user.getIdTokenResult(),
        !skipEncumbrancesCheck && checkEncumbrances(),
      ]);
      const enrichedUser = {
        ...user,
        // from Identity API
        pronouns: identity.pronouns,
        preferredName: identity.preferredName,
        lastName: identity.lastName,
        displayName: identity.displayName,
        email: identity.primaryEmail,
        studentId: identity.callistaPersonID || null,
        staffId: identity.sapEmployeeID || null,
        successfulIdentityResponse:
          identity.callistaPersonID || identity.sapEmployeeID,
        is: identity.is,
        organizationalRelationships: identity.organizationalRelationships,

        // from Academic Results API
        ...encumbrances,

        // from token
        roles: idToken.claims?.roles,
      };
      env !== 'prod' && console.log('signed-in user: ', enrichedUser);
      setUser(enrichedUser);
      sessionStorage.setItem('postLoginBuffer', 'true');
    } catch {
      setUser(user);
      sessionStorage.setItem('postLoginBuffer', 'true');
    }

    if (getPreferences) {
      try {
        const doc = await getPortalPreferences();
        setUserDoc(doc);
      } catch (error) {
        console.warn(
          '[getPortalPreferences]: API call error, failed to get user doc.',
          error,
        );
      }
      setLoadingUserDoc(false);
    }
  };

  useEffect(() => {
    const isFirebaseAuthLoginAttempted = sessionStorage.getItem(
      'isFirebaseAuthLoginAttempted',
    );
    if (isFirebaseAuthLoginAttempted !== 'true') {
      // If isFirebaseAuthLoginAttempted is false, it means that the user is not logged in yet or is waiting for an Okta callback
      if (window.location.pathname === callbackPath) {
        // If the path is an Okta callback path, then frontend needs to pass authorisation code and state to backend in exchange for a Firebase custom token
        handleCallback();
      } else if (nolanding) {
        // nolanding is always true for now
        loginOkta(targetLocation);
      }
    } else {
      // If isFirebaseAuthLoginAttempted is true, it means that the user has logged in with Firebase Auth
      setRedirecting(false);
      onAuthStateChanged(auth, (user) => {
        if (user) {
          getUserToken(user);
        }
      });
    }
  }, [window.location.pathname]);

  useEffect(() => {
    if (redirectUrl && !redirecting) {
      window.location.replace(redirectUrl);
      setRedirecting(true);
    }
  }, [redirectUrl, redirecting]);

  const loginOkta = (targetLocation) => {
    env !== 'prod' && console.log('---- Step 1: Redirect to Okta ----');
    setRedirectUrl(
      `${oktaLoginUrl}?redirect_uri=${encodeURIComponent(
        targetLocation || window.location,
      )}`,
    );
  };

  const logout = async (redirect) => {
    localStorage.clear();
    sessionStorage.clear();
    sessionStorage.setItem('isFirebaseAuthLoginAttempted', 'false');
    if (user?.roles?.includes('studentportal-support-impersonate')) {
      await Promise.all([endImpersonation(), user?.auth.signOut()]);
    } else {
      await user?.auth.signOut();
    }
    if (redirect) window.open(redirect, '_self');
  };

  const getCallbackParams = () => {
    if (window.location.search === '') return { code: null, state: null };
    // Get authorisation code and state from the Okta callback URL
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');
    const errorCode = urlParams.get('error');
    const errorDescription = urlParams.get('error_description');
    if (errorCode) {
      if (errorCode === 'access_denied') {
        window.location.replace(
          'https://www.monash.edu/esolutions/accounts-passwords/access',
        );
      }
      const error = new Error(errorDescription);
      error.code = errorCode;
      throw error;
    }
    return { code, state };
  };

  const handleCallback = async () => {
    env !== 'prod' &&
      console.log('---- Step 2: Handle callback from Okta ----');
    if (!redirecting) {
      // Always redirect to the loading page when waiting for a Firebase custom token
      setRedirecting(true);
      try {
        if (!user) {
          const { code, state } = getCallbackParams();
          const resp = await fetch(`${tokenApi}?code=${code}&state=${state}`, {
            mode: 'cors',
          });
          if (resp.status === 403) {
            window.location.replace(
              'https://www.monash.edu/esolutions/accounts-passwords/access',
            );
            return;
          }
          const authResult = await resp.json();
          await setPersistence(auth, browserSessionPersistence);
          await signInWithCustomToken(auth, authResult[FIREBASE_CUSTOM_TOKEN]);
          // Session storage does not support boolean values, so we use "true" as a flag to indicate that the user has logged in with Firebase Auth
          // Also add a postLoginBuffer for the rendering of login text in loadingscreen, for preinitialised, post auth users.
          sessionStorage.setItem('isFirebaseAuthLoginAttempted', 'true');
          sessionStorage.setItem('postLoginBuffer', 'false');
          env !== 'prod' &&
            console.log(
              `---- Step 3: Signed in with Firebase custom token and redirect back to ${authResult.redirect_url} ----`,
            );
          // Once the Firebase custom token has been obtained, redirect to the target location
          setRedirecting(false);
          setRedirectUrl(authResult.redirect_url);
        }
      } catch (error) {
        setRedirecting(false);
        setError(error);
        const errorCode = error.code;
        const errorMessage = error.message;
        console.error(errorCode, errorMessage);
      }
    }
  };

  const checkIdentity = () => {
    return (
      // DELETE THIS LATER
      window.location.hostname.includes('-dev') ||
      window.location.hostname === 'localhost' ||
      //
      user?.successfulIdentityResponse
    );
  };

  const isBlocked =
    user?.isBarred || user?.encumbranceEffectTypes?.includes('RVK_SRVC');

  const finalPage =
    accessControl(user) && checkIdentity() && !isBlocked ? (
      props.children
    ) : (
      <AccessDenied blocked={isBlocked} />
    );

  const content = user ? finalPage : loadingPage;

  const [isLoading, setIsLoading] = useState(showLoadingPage);
  const [fadeOut, setFadeOut] = useState(false);

  useEffect(() => {
    if (showLoadingPage) {
      setIsLoading(true);
      setFadeOut(false); // Reset fadeOut when loading page is shown
    } else if (user) {
      // Start fade-out effect when user is available and loading page is shown
      setFadeOut(true);
      // Wait for the fade-out transition to complete before changing the page
      const timer = setTimeout(() => {
        setIsLoading(false);
      }, 200);

      return () => clearTimeout(timer);
    }
  }, [user, showLoadingPage]);

  const loadingPageStyle = {
    height: '100%',
    opacity: fadeOut ? 0 : 1,
    transition: 'opacity 0.2s ease-out',
  };
  return (
    <AuthContext.Provider
      value={{
        user,
        error,
        userDoc,
        loginOkta,
        logout,
      }}
    >
      {/* If redirecting is true, the loading page is displayed (possible scenarios: redirecting to Okta or waiting for a Firebase custom token to be issued from backend). */}
      {/* Otherwise, if user is not null, which means that the Firebase auth user has been created in the browser Session Storage, then the child component is displayed */}

      {isLoading && <div style={loadingPageStyle}>{loadingPage}</div>}
      {!isLoading && user && content}
    </AuthContext.Provider>
  );
};

export { StandardAuthProvider };
